import InspirationBentoGrid, { mapInspirationCategories } from "@/components/organisms/inspiration-bento-grid";
import OurServices from "@/components/organisms/our-services";
import StageHeader from "@/components/organisms/stage-header";
import { generateSEO } from "@/helpers/seo";
import { appLocales, getPrismicLocale } from "@/lib/i18n";
import { getAllByType, getAllByUids, getSingle } from "@/lib/prismic";
import { InspirationCategoryDocument } from "@/prismicio-types";
import { Content } from "@prismicio/client";
import { notFound } from "next/navigation";
import { use } from "react";

type InspirationsProps = {
  resolution: Resolution;
};

const Inspirations = ({ resolution }: InspirationsProps) => {
  const heroData = use(getSingle<Content.ArchiveInspirationDocument>("archive_inspiration", resolution.locale));

  let inspirationCategoriesResult: InspirationCategoryDocument[] | null = null;
  inspirationCategoriesResult = use(
    getAllByType("inspiration_category", resolution.locale, {
      fetch: ["inspiration_category.title", "inspiration_category.featuredImage", "inspiration_category.display"],
      orderings: {
        field: "my.inspiration_category.display",
        direction: "desc",
      },
    })
  ) as InspirationCategoryDocument[] | null;

  if (resolution.locale !== appLocales.defaultLocale) {
    const frenchInspirationUids =
      inspirationCategoriesResult?.map(
        (inspCat) =>
          inspCat.alternate_languages.find((alt) => alt.lang === getPrismicLocale(appLocales.defaultLocale))?.uid ||
          inspCat.uid
      ) || [];

    const frenchInspirationCategories = use(
      getAllByUids("inspiration_category", frenchInspirationUids, appLocales.defaultLocale, {
        fetch: ["inspiration_category.title", "inspiration_category.featuredImage", "inspiration_category.display"],
        orderings: {
          field: "my.inspiration_category.display",
          direction: "desc",
        },
      })
    ) as InspirationCategoryDocument[] | null;

    inspirationCategoriesResult = inspirationCategoriesResult
      ?.map((localeInspirationCategory) => {
        const frenchEquivalent = frenchInspirationCategories?.find(
          (frenchInspirationCategory) =>
            frenchInspirationCategory.alternate_languages.find(
              (alt) => alt.lang === getPrismicLocale(resolution.locale)
            )?.uid === localeInspirationCategory.uid
        );

        if (frenchEquivalent) {
          return {
            ...localeInspirationCategory,
            data: {
              ...localeInspirationCategory.data,
              featuredImage: frenchEquivalent.data.featuredImage || localeInspirationCategory.data.featuredImage,
            },
          };
        }
        return undefined;
      })
      .filter(Boolean) as InspirationCategoryDocument[];
  }

  if (!heroData || !inspirationCategoriesResult || inspirationCategoriesResult.length === 0) return notFound();

  const { title, introduction } = heroData.data;
  const inspirationCategories = mapInspirationCategories(inspirationCategoriesResult, resolution.locale);

  return (
    <>
      <StageHeader
        locale={resolution.locale}
        className="md:my-4 max-md:[&_h1]:mt-4"
        orientationOnMobile="center"
        hideDescriptionOnMobile
        title={title}
        description={introduction}
      />
      <InspirationBentoGrid
        inspirationCategories={inspirationCategories.sort((a, b) => {
          return (a.display || 9999) - (b.display || 9999);
        })}
      />
      <OurServices locale={resolution.locale} className="lg:hidden" />
    </>
  );
};

export default Inspirations;

Inspirations.generateMetadata = async (resolution: Resolution) => {
  const document = await getSingle<Content.ArchiveInspirationDocument>("archive_inspiration", resolution.locale);

  if (!document || !document.data) return {};

  return generateSEO(document, resolution.locale);
};
